import { KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { useNextT } from "../../i18n";
import { ToastHost } from "../Toast";
import s from "./DatePicker.module.scss";

export type DatePickerLabels = { title: string; prevMonth: string; nextMonth: string; today: string; clear: string; locale: string };
type Props = {
  open: boolean;
  /** Y-m-d */
  value?: string | null;
  onChange: (value: string | null) => void;
  onClose: () => void;
  /** false: days after today are disabled (a report date). true: every day can be picked (a filter). */
  allowFuture: boolean;
  /** Y-m-d: days before it are disabled (e.g. a reminder: today or later). Works with either allowFuture. */
  minDate?: string;
  labels: DatePickerLabels;
};

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (v: string) => { const [y, m, d] = v.split("-").map(Number); return new Date(y, m - 1, d); };
const isYmd = (v: unknown): v is string => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && ymd(parse(v)) === v;
const addDays = (v: string, n: number) => { const d = parse(v); d.setDate(d.getDate() + n); return ymd(d); };
const addMonths = (v: string, n: number) => { const d = parse(v); const day = d.getDate(); d.setDate(1); d.setMonth(d.getMonth() + n); d.setDate(Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate())); return ymd(d); };

/** First day of the week for a locale: 0 = Sunday ... 6 = Saturday. Intl when the engine knows it, else by language. */
export const weekStart = (locale: string): number => {
  try {
    const l = new Intl.Locale(locale) as any;
    const info = typeof l.getWeekInfo === "function" ? l.getWeekInfo() : l.weekInfo;
    if (info?.firstDay) return info.firstDay % 7;
  } catch { /* fall through */ }
  return ["fr", "de", "es", "it", "ru", "pt", "nl"].includes(locale.slice(0, 2)) ? 1 : 0;
};

const Calendar = ({ value, onChange, onClose, allowFuture, minDate, labels }: Omit<Props, "open">) => {
  const { isRtl } = useNextT();
  const id = useId();
  const today = ymd(new Date());
  const min = isYmd(minDate) ? minDate : null;
  const empty = !allowFuture && min !== null && min > today;
  useEffect(() => {
    // eslint-disable-next-line no-console -- a developer mistake, not a user message
    if (empty && process.env.NODE_ENV !== "production") console.warn(`DatePicker: no day can be picked (minDate ${min} is after today and allowFuture is false).`);
  }, [empty, min]);
  const blocked = (v: string) => (!allowFuture && v > today) || (min !== null && v < min);
  // A malformed value, or one that may not be picked, must not leave the grid without a tab stop: start on today,
  // or on the first day that may be picked when today is before it.
  const start = isYmd(value) && !blocked(value) ? value : min !== null && today < min ? min : today;
  const [focused, setFocused] = useState(start);
  const kbd = useRef(false);
  const root = useRef<HTMLDivElement>(null);
  const first = weekStart(labels.locale);
  const view = parse(focused);
  const year = view.getFullYear(); const month = view.getMonth();

  // After an arrow key moved the focused day (possibly into another month), put the real focus on its button.
  useEffect(() => {
    if (!kbd.current) return;
    kbd.current = false;
    root.current?.querySelector<HTMLElement>(`[data-ymd="${focused}"]`)?.focus();
  }, [focused]);

  const monthName = new Intl.DateTimeFormat(labels.locale, { month: "long", year: "numeric" }).format(new Date(year, month, 1));
  const weekdays = Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(labels.locale, { weekday: "short" }).format(new Date(2026, 9, 4 + ((first + i) % 7))));
  const dayName = new Intl.DateTimeFormat(labels.locale, { dateStyle: "long" });
  const lead = (new Date(year, month, 1).getDay() - first + 7) % 7;
  const count = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [...Array(lead).fill(null), ...Array.from({ length: count }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const weeks = Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
  const nextMonthBlocked = blocked(ymd(new Date(year, month + 1, 1)));
  const prevMonthBlocked = blocked(ymd(new Date(year, month, 0)));
  // A month arrow keeps the focused day pickable (so the grid always has a working tab stop).
  const clamp = (v: string) => (min !== null && v < min ? min : !allowFuture && v > today ? today : v);

  const move = (target: string) => { if (blocked(target)) return; kbd.current = true; setFocused(target); };
  // A month jump that lands outside the window stops at its edge (the first or last day that may be picked).
  const jump = (target: string) => move(clamp(target));
  const onKeyDown = (e: KeyboardEvent) => {
    const step = isRtl ? -1 : 1;
    const by: Record<string, () => string> = {
      ArrowRight: () => addDays(focused, step), ArrowLeft: () => addDays(focused, -step),
      ArrowDown: () => addDays(focused, 7), ArrowUp: () => addDays(focused, -7),
      PageDown: () => addMonths(focused, 1), PageUp: () => addMonths(focused, -1),
    };
    if (!by[e.key]) return;
    e.preventDefault();
    (e.key === "PageUp" || e.key === "PageDown" ? jump : move)(by[e.key]());
  };
  const pick = (v: string | null) => { onChange(v); onClose(); };

  return (
    <div ref={root} className={s.calendar}>
      <div className={s.nav}>
        <IconButton label={labels.prevMonth} icon={isRtl ? <ChevronRightRoundedIcon /> : <ChevronLeftRoundedIcon />} disabled={prevMonthBlocked} onClick={() => setFocused(clamp(addMonths(focused, -1)))} />
        <h2 id={`${id}-month`} className={s.month} aria-live="polite">{monthName}</h2>
        <IconButton label={labels.nextMonth} icon={isRtl ? <ChevronLeftRoundedIcon /> : <ChevronRightRoundedIcon />} disabled={nextMonthBlocked} onClick={() => setFocused(clamp(addMonths(focused, 1)))} />
      </div>
      <div role="grid" aria-labelledby={`${id}-month`} onKeyDown={onKeyDown}>
        <div role="row" className={s.week}>
          {weekdays.map((w, i) => <span key={i} role="columnheader" className={s.dow}>{w}</span>)}
        </div>
        {weeks.map((w, wi) => (
          <div role="row" key={wi} className={s.week}>
            {w.map((d, di) => {
              if (d === null) return <span key={di} role="gridcell" aria-hidden="true" />;
              const v = ymd(new Date(year, month, d));
              return (
                <span key={di} role="gridcell">
                  <button type="button" data-ymd={v} tabIndex={v === focused ? 0 : -1} disabled={blocked(v)}
                    className={`${s.day} ${s.tap}${v === today ? ` ${s.today}` : ""}${v === value ? ` ${s.on}` : ""}`}
                    aria-label={dayName.format(new Date(year, month, d))} aria-current={v === today ? "date" : undefined} aria-pressed={v === value} onClick={() => pick(v)}>{d}</button>
                </span>
              );
            })}
          </div>
        ))}
      </div>
      <div className={s.actions}>
        <Button variant="ghost" disabled={blocked(today)} onClick={() => pick(today)}>{labels.today}</Button>
        <Button onClick={() => pick(null)}>{labels.clear}</Button>
      </div>
    </div>
  );
};

/** A month grid in a small dialog. Dates are Y-m-d strings in local time. */
export const DatePicker = ({ open, onClose, labels, ...rest }: Props) => (
  // The name goes on the paper: that is the element with role="dialog" (MUI puts a root aria-label elsewhere).
  <Dialog open={open} onClose={onClose} className="cy-next" PaperProps={{ className: s.paper, "aria-label": labels.title }}>
    <Calendar {...rest} onClose={onClose} labels={labels} />
    <ToastHost active={open} />
  </Dialog>
);
